import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash, UserPlus, Envelope, Copy, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["admin", "coordinator", "accountant"];

const ROLE_HINT = {
  admin: "Full access — team, financials, deletes.",
  coordinator: "Runs ops — clients, interpreters, projects.",
  accountant: "Manages invoices, marks paid, reports.",
};

export default function Team() {
  const qc = useQueryClient();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("coordinator");

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const { data: invites = [] } = useQuery({
    queryKey: ["team-invites"],
    queryFn: async () => (await api.get("/team/invites")).data,
  });

  const updateRole = useMutation({
    mutationFn: async ({ user_id, role }) => (await api.patch(`/users/${user_id}/role`, { role })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Role updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const removeUser = useMutation({
    mutationFn: async (user_id) => (await api.delete(`/users/${user_id}`)).data,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success(`Removed ${r.removed}`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not remove"),
  });

  const invite = useMutation({
    mutationFn: async ({ email, role }) => (await api.post("/team/invites", { email, role })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-invites"] });
      toast.success("Invite created — share the sign-in link with them.");
      setInviteEmail("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not invite"),
  });

  const revoke = useMutation({
    mutationFn: async (email) => (await api.delete(`/team/invites/${encodeURIComponent(email)}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team-invites"] }); toast.success("Invite revoked"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to revoke"),
  });

  const copySignInLink = () => {
    const url = `${window.location.origin}/`;
    navigator.clipboard?.writeText(url)?.catch(() => {});
    toast.success("Sign-in link copied");
  };

  return (
    <div data-testid="team-page">
      <PageHeader
        eyebrow="Administration"
        title="Team"
        description="Invite teammates, assign their role, and revoke access. Only invited email addresses can sign in."
      />

      {/* Invite panel */}
      <div className="card-flat p-5 mb-6" data-testid="invite-panel">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} weight="duotone" className="text-blue-800" />
          <h3 className="text-sm font-semibold tracking-wide uppercase text-slate-700">Invite by email</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_auto_auto] gap-3">
          <Input
            type="email"
            placeholder="teammate@unitylanguagegroup.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            data-testid="invite-email-input"
            className="h-10"
          />
          <Select value={inviteRole} onValueChange={setInviteRole}>
            <SelectTrigger data-testid="invite-role-select" className="h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => (
                <SelectItem key={r} value={r} data-testid={`invite-role-${r}`}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => {
              const e = inviteEmail.trim().toLowerCase();
              if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
              invite.mutate({ email: e, role: inviteRole });
            }}
            disabled={invite.isPending}
            className="bg-blue-900 hover:bg-blue-800 text-white h-10"
            data-testid="invite-submit"
          >
            <UserPlus size={16} className="mr-2" /> Invite
          </Button>
          <Button
            variant="outline"
            onClick={copySignInLink}
            className="h-10"
            data-testid="copy-signin-link"
          >
            <Copy size={16} className="mr-2" /> Copy sign-in link
          </Button>
        </div>
        <div className="text-[11px] text-slate-500 mt-3 leading-relaxed">
          Invited teammates sign in with their Google account at the copied link. Only emails you invite can create
          an account — everyone else is blocked. <span className="text-slate-600 font-medium">{ROLE_HINT[inviteRole]}</span>
        </div>

        {invites.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">
              Pending invites ({invites.length})
            </div>
            <div className="space-y-2">
              {invites.map((iv) => (
                <div key={iv.email} className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`invite-row-${iv.email}`}>
                  <Envelope size={16} className="text-amber-700" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-slate-800 truncate">{iv.email}</div>
                    <div className="text-[11px] text-slate-500">
                      Role: <b>{iv.role}</b> · invited by {iv.invited_by} · {fmtDate(iv.created_at)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (window.confirm(`Revoke invite for ${iv.email}?`)) revoke.mutate(iv.email);
                    }}
                    data-testid={`invite-revoke-${iv.email}`}
                  >
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Active team members */}
      <div className="card-flat overflow-x-auto">
        <div className="px-5 py-3 border-b border-slate-100 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
          Active team members ({users.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Name</th>
              <th className="text-left px-3 py-3 font-semibold">Email</th>
              <th className="text-left px-3 py-3 font-semibold">Joined</th>
              <th className="text-left px-3 py-3 font-semibold">Role</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="data-row border-t border-slate-100" data-testid={`team-row-${u.user_id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{u.name}</td>
                <td className="px-3 py-3 text-slate-700 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-3 text-slate-500 text-xs">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-3">
                  <Select value={u.role} onValueChange={(v) => updateRole.mutate({ user_id: u.user_id, role: v })}>
                    <SelectTrigger className="w-40" data-testid={`role-${u.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((o) => <SelectItem key={o} value={o} data-testid={`role-${u.user_id}-${o}`}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-5 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => {
                      if (window.confirm(`Remove ${u.email}? They will lose access immediately.`)) removeUser.mutate(u.user_id);
                    }}
                    data-testid={`remove-${u.user_id}`}
                  >
                    <Trash size={14} className="mr-1" /> Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
